import ApiRequest from '../../Decorators/ApiRequest';
import { CustomProjectsResponse } from './Interfaces/CustomProjectsResponse';
import { IsDisabledQuestionResponse } from './Interfaces/IsDisabledQuestionResponse';
import { ApiResult } from '@shared/Interfaces/Api/ApiResult';
import { API_DEFAULT_HEADERS } from '@configs/ApiDefaultHeaders';
import {
    CustomProjectEntity,
    RawCustomProject,
} from '@entities/CustomProject/Types';
import { QuestionEntity } from '@entities/Question/Types';
import { RawParsedFragment } from '@entities/ParsedFragment/Types';
import { ProjectAddItemPayload } from './Interfaces/ProjectAddItemPayload';
import { ProjectOrderedQtyPayload } from './Interfaces/ProjectItemOrderedQtyPayload';
import { SortParamEnum } from '@shared/Enums/SortParamEnum';

const BASE_URL = import.meta.env.VITE_BASE_URL_CUSTOMER;

export class CustomProjectsGateway {
    async getCustomProjectsList(
        page = 1,
        perPage = 5,
        sortField = 'created_at',
        sortOrder = SortParamEnum.DESC
    ): Promise<ApiResult<CustomProjectsResponse>> {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('perPage', perPage.toString());
        queryParams.append('sortField', sortField);
        queryParams.append('sortOrder', sortOrder);
        ['title', 'created_at', 'target_date'].forEach(field => {
            queryParams.append('with[]', field);
        });

        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects?${queryParams}`,
                {
                    method: 'GET',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to get projects',
                };
            }
            const json = await response.json();
            return {
                error: false,
                data: json as CustomProjectsResponse,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async getCustomProjectById(
        projectId: number
    ): Promise<ApiResult<RawCustomProject>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}`,
                {
                    method: 'GET',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to get the project',
                };
            }

            const json = await response.json();

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async deleteCustomProjectById(
        projectId: number
    ): Promise<ApiResult<boolean>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}`,
                {
                    method: 'DELETE',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to delete project',
                };
            }

            return {
                error: false,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async lockProject(projectId: number): Promise<ApiResult<RawCustomProject>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/lock`,
                {
                    method: 'PATCH',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to lock the project',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async checkDisabledQuestion(
        projectId: number,
        answerUuid: string,
        question: QuestionEntity
    ): Promise<ApiResult<IsDisabledQuestionResponse>> {
        const currentAnswer = question.answers?.find(
            answer => answer.uuid === answerUuid
        )?.value;

        const body = {
            data: question.answers?.map(answer => ({
                ...answer,
                value: String(answer.value),
            })),
        };
        const url = `projects/${projectId}/check_disabled_question?template_index=0&question[${question.key}]=${Number(currentAnswer)}`;

        try {
            const response = await ApiRequest(url, {
                method: 'POST',
                headers: API_DEFAULT_HEADERS,
                body: JSON.stringify(body),
            });

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed check is disabled question',
                };
            }

            return {
                error: false,
                data: json as IsDisabledQuestionResponse,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async updateProjectById(
        project: CustomProjectEntity
    ): Promise<ApiResult<RawCustomProject>> {
        if (!project.templates) {
            return {
                error: true,
                message: 'TemplatesViewPage not found',
            };
        }

        const updatedTemplates = project.templates.map(template =>
            template.transformTemplateForUpdate()
        );
        const updatedProjectData = {
            title: project.title,
            target_date: project.targetDate,
            data: {
                templates: updatedTemplates,
            },
        };
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${project.id}`,
                {
                    method: 'PATCH',
                    headers: API_DEFAULT_HEADERS,
                    body: JSON.stringify(updatedProjectData),
                }
            );

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to update project',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async getParsedFragments(
        projectId: number,
        templateIndex: number = 0
    ): Promise<ApiResult<RawParsedFragment[]>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/pars_nested?template_index=${templateIndex}`,
                {
                    method: 'GET',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            const json = await response.json();

            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to fetch parsed fragments',
                };
            }

            return {
                error: false,
                data: json as RawParsedFragment[],
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async flushProject(
        projectId: number
    ): Promise<ApiResult<RawCustomProject>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/flush`,
                {
                    method: 'PATCH',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            const json = await response.json();

            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to flush project',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async minimumPriceProject(
        projectId: number
    ): Promise<ApiResult<RawCustomProject>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/min_price`,
                {
                    method: 'PATCH',
                    headers: API_DEFAULT_HEADERS,
                }
            );

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to fetch minimum price project',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async addItem(
        projectId: number,
        payload: ProjectAddItemPayload[]
    ): Promise<ApiResult<RawCustomProject>> {
        const body = {
            data: payload,
        };

        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/add_item`,
                {
                    method: 'PATCH',
                    headers: API_DEFAULT_HEADERS,
                    body: JSON.stringify(body),
                }
            );

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to add item to project',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async addItemOrderedQty(
        projectId: number,
        orderedQtyPayload: ProjectOrderedQtyPayload[]
    ): Promise<ApiResult<RawCustomProject>> {
        const body = { data: orderedQtyPayload };

        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/item_ordered_qty`,
                {
                    method: 'PATCH',
                    headers: API_DEFAULT_HEADERS,
                    body: JSON.stringify(body),
                }
            );

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to add item ordered quantity',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async updateAddress(
        projectId: number,
        fullAddress: string,
        latitude: string,
        longitude: string
    ): Promise<ApiResult<RawCustomProject>> {
        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/${projectId}/address`,
                {
                    method: 'PATCH',
                    body: JSON.stringify({
                        address: {
                            full_address: fullAddress,
                            latitude,
                            longitude,
                        },
                    }),
                    headers: API_DEFAULT_HEADERS,
                }
            );

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to update address',
                };
            }

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async createCustomProjectWithFreeTemplate(
        templateIds: number[]
    ): Promise<ApiResult<RawCustomProject>> {
        if (!templateIds || templateIds.length < 1) {
            return {
                error: true,
                message: 'No template was found.',
            };
        }

        try {
            const response = await ApiRequest(
                `${BASE_URL}/projects/create_with_free_template`,
                {
                    method: 'POST',
                    body: JSON.stringify({ templates: templateIds }),
                    headers: API_DEFAULT_HEADERS,
                }
            );

            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to create new project',
                };
            }

            const json = await response.json();

            return {
                error: false,
                data: json as RawCustomProject,
            };
        } catch (error) {
            console.error(`Error: ${error}`);
            return {
                error: true,
                message: `Failed to create new project`,
            };
        }
    }
}
